<script setup>
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import { Head, Link } from '@inertiajs/vue3'
import { ref, onMounted } from 'vue';
import IconField from "primevue/iconfield";
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css"
import * as L from 'leaflet';

const props = defineProps({ dealers: Array, latitude: Number, longitude: Number });

const searchValue = ref('');

const initialMap = ref(null);

onMounted(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    initialMap.value = L.map('map', {
        scrollWheelZoom: !isTouchDevice,
        tap: !isTouchDevice,
        touchZoom: isTouchDevice ? 'center' : true,
    }).setView([52.154, 5.295], 8);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        minZoom: 8,
        maxZoom: 15,
        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(initialMap.value);

    props.dealers.forEach((dealer) => {
        const marker = L.marker([dealer.address.latitude, dealer.address.longitude], {
            icon: L.icon({
                iconUrl: '/img/marker-icon-blue.png',
                shadowUrl: '/img/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41]
            })
        }).addTo(initialMap.value);
        marker._icon.id = dealer.id;
        marker.bindPopup(dealer.company_name);
        marker.on('click', onClick);
    });

    //add marker if latitude and longitude are provided
    if (props.latitude && props.longitude) {
        //add the marker with a different color

        const marker = L.marker([props.latitude, props.longitude], {
            icon: L.icon({
                iconUrl: '/img/marker-icon-red.png',
                shadowUrl: '/img/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41]
            })
        }).addTo(initialMap.value);
    }

    if (!isTouchDevice) {
        // Enable scroll wheel zoom on map click for non-touch devices
        initialMap.value.on('click', () => {
            initialMap.value.scrollWheelZoom.enable();
        });

        // Disable scroll wheel zoom when clicking outside the map for non-touch devices
        document.addEventListener('click', (event) => {
            if (!document.getElementById('map').contains(event.target)) {
                initialMap.value.scrollWheelZoom.disable();
            }
        });
    }
});
const profile = ref({
    id: '',
    name: '',
    contactName: '',
    website: '',
    email: '',
    phone_number: '',
    whatsapp_number: '',
    address: '',
    vat_number: '',
    chamber_of_commerce_number: '',
})

function onClick(e) {
    var dealerId = e.target._icon.id;
    var dealer = props.dealers.find(dealer => dealer.id == dealerId);
    if (!dealer) {
        profile.value = {
            id: '',
            name: 'Er is een fout opgetreden',
            contactName: '',
            website: '',
            email: '',
            phone_number: '',
            whatsapp_number: '',
            address: '',
            vat_number: '',
            chamber_of_commerce_number: '',
            description: 'Kon geen dealer informatie vinden'
        };
    } else {
        var newProfile = {};
        newProfile.name = dealer.company_name;
        newProfile.id = dealer.id;
        newProfile.contactName = dealer.name;
        newProfile.website = dealer.website;
        newProfile.email = dealer.email;
        newProfile.phone_number = dealer.phone_number;
        newProfile.whatsapp_number = dealer.whatsapp_number;
        newProfile.address = dealer.address.route + ' ' + dealer.address.street_number + ', ' + dealer.address.postal_code + ' ' + dealer.address.locality;
        newProfile.vat_number = dealer.vat_number;
        newProfile.chamber_of_commerce_number = dealer.chamber_of_commerce_number;
        newProfile.description = dealer.description;
        profile.value = newProfile;
    }
}
</script>

<template>
    <Head title="Dealers kaart" />
    <main class="px-4 sm:px-6 py-6 space-y-8">
        <div>
            <nav class="sm:hidden" aria-label="Back">
                <Link href="/portal/dealers" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-x-1">
                    <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-3 w-3 text-gray-400 shrink-0" />
                    Terug
                </Link>
            </nav>
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
                <ol role="list" class="flex items-center space-x-4">
                    <li>
                        <div class="flex">
                            <Link href="/portal/dashboard"
                                class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard</Link>
                        </div>
                    </li>
                    <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
                    <li>
                        <div class="flex items-center">
                            <Link href="/portal/dealers" class="text-sm font-medium text-gray-500 hover:text-gray-700">
                            Dealers</Link>
                        </div>
                    </li>
                    <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
                    <li>
                        <div class="flex items-center">
                            <Link href="#" class="text-sm font-medium text-gray-900 hover:text-gray-700">
                            Kaart</Link>
                        </div>
                    </li>
                </ol>
            </nav>
        </div>
        <div class="flex flex-col sm:flex-row justify-between w-full gap-y-2">
            <div class="flex flex-col">
                <h1 class="text-base font-semibold leading-6 text-gray-900">Dealers</h1>
                <p class="mt-2 text-sm text-gray-700">Een overzicht van alle dealers in een tabel.</p>
            </div>
            <div class="flex flex-row gap-3 items-center">
                <IconField iconPosition="right" class="w-full sm:w-auto">
                    <InputIcon class="pi pi-search"> </InputIcon>
                    <InputText class="h-10" v-model="searchValue" placeholder="Search" />
                </IconField>
                <Link href="/portal/dealers" class="cursor-pointer">
                <font-awesome-icon :icon="['fal', 'list']" class="w-5 h-5 p-2.5 border p-2 hover:bg-black/5 text-gray-900 shadow-sm rounded-lg shrink-0" aria-hidden="true" />
                </Link>
                <Link href="/portal/dealer/aanmaken" class="cursor-pointer">
                    <font-awesome-icon :icon="['fal', 'plus']" class="w-5 h-5 p-2.5 bg-royalblue-500 hover:bg-royalblue-500/80 text-white shadow-sm rounded-lg shrink-0" aria-hidden="true" />
                </Link>
            </div>
        </div>
        <div class="grid lg:grid-cols-3 gap-10 h-[50rem]">
            <div class="w-full col-span-2">
                <div id="map" class="h-full z-0 ring-1 rounded-lg shadow-md"></div>
            </div>
            <div class="w-full ring-1 rounded-lg shadow-md flex flex-col gap-6 overflow-hidden">

                <div class="space-y-4" v-if="profile.name">
                    <div class="relative">
                        <div class="absolute bottom-4 left-4 gap-4 flex">
                            <Link :href="'/portal/dealer/'+profile.id" type="button"
                                class="w-auto rounded-md bg-royalblue-500 px-3 py-2 text-sm font-semibold text-nowrap text-white shadow-sm hover:bg-royalblue-500/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royalblue-500">
                            Bekijk dealer informatie
                            </Link>
                        </div>
                        <div class="absolute bottom-4 right-4 gap-4 flex">
                            <Link :href="'/portal/dealer/' + profile.id + '/edit'" type="button"
                                class="inline-flex justify-center rounded-md bg-white px-2 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                <font-awesome-icon :icon="['far', 'pencil']" class="h-5 w-5 text-gray-400 shrink-0"/>
                                <span class="sr-only">Bekijk dealer</span>
                            </Link>
                        </div>
                        <img class="h-18 w-full object-cover lg:h-24"
                            src="https://images.unsplash.com/photo-1444628838545-ac4016a5418a?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
                            alt="" />
                    </div>
                    <div class="p-4 space-y-8 h-full">
                        <h1 class="truncate text-2xl font-bold text-gray-900" v-text="profile.name"></h1>
                        <h3 class="text-lg font-semibold leading-7 text-gray-900">Informatie</h3>
                        <p class="mt-4 text-sm leading-6 text-gray-500"
                            v-text="profile.description ? profile.description : 'Er is geen informatie voor deze dealer opgegeven'">
                        </p>
                        <div class="grid grid-cols-1 divide-y divide-gray-100">
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">Contact persoon</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    v-text="profile.contactName"></dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">Telefoon nummer (Whatsapp / 06)</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                    <a :href="'https://wa.me/'+profile.whatsapp_number" target="_blank" class="text-royalblue-500 hover:text-royalblue-700"
                                        v-text="profile.whatsapp_number"></a>
                                </dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">Telefoon nummer (vaste lijn)</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                    <a :href="'tel:'+profile.phone_number" v-text="profile.phone_number" class="text-royalblue-500 hover:text-royalblue-700"></a>
                                </dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">Email address</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    v-text="profile.email"></dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">Website</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    v-text="profile.website"></dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">Adres</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    v-text="profile.address">
                                </dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">BTW-nummer</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    v-text="profile.vat_number">
                                </dd>
                            </div>
                            <div class="py-3 flex flex-row justify-between">
                                <dt class="text-sm font-medium leading-6 text-gray-900">KVK-nummer</dt>
                                <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    v-text="profile.chamber_of_commerce_number">
                                </dd>
                            </div>
                        </div>
                        <div class="h-full w-full mt-4">
                        </div>
                    </div>
                </div>
                <div v-else class="p-4">
                    <span v-if="props.latitude && props.longitude" class="text-sm text-gray-500">
                        Klik op een blauwe marker om de dealer informatie te zien. De rode marker is waar de kavel zich bevindt.
                    </span>
                    <span v-else class="text-sm text-gray-500">
                        Klik op een marker om de informatie te tonen.
                    </span>
                </div>
            </div>
        </div>
    </main>
</template>
